import { Card, CardBody, CardHeader } from '@nextui-org/react'
import { useState } from 'react'
import { useAsync } from 'react-use'
import { InputOutputExamples } from '~/data/InputOutputExamples'
import useFlowNamesCampaign from '~/hooks/task/useFlowNamesCampaign'
import { AgentRequest, AgentTaskStatus, CommandType, RuntimeSettings } from '~/protos/agents'
import { IconCurieStarButton } from '../icons'
import { TaskParams } from '../modals/task-forms/BaseTaskForm'
import { SimpleMultiSelect } from '../SimpleMultiSelect'
import { CampaignTaskProgressBar } from './campaignBlocks/CampaignTaskProgressBar'
import { CampaignButton } from './CampaignButton'
import CampaignChat from './CampaignChat'
import { CampaignInputOutputToDWLForm } from './CampaignInputOutputExamples'
import MuleProjectUpload from './MuleProjectUpload'

interface CampaignTryOwnDataProps {
  commandType?: CommandType
  currentGenerationSection: string
  handleChangeGenerationSection: (section: string) => void
  handleSetError: (error: boolean) => void
  handleSetRequest: (request: AgentRequest) => void
  handleSubmit: (request: AgentRequest) => void
  handleTryExample: () => void
  isDisabled: boolean
  isError: boolean
  messages: Message[]
  ownDataRef: React.RefObject<HTMLDivElement>
  preSubmitCheck: (request: AgentRequest, section: string) => boolean
  progress: number
  request: AgentRequest
  resetTaskProgress: () => void
  taskStatus: AgentTaskStatus
}

const DataWeaveGenerator = ({
  currentGenerationSection,
  handleChangeGenerationSection,
  handleSetError,
  handleSetRequest,
  handleSubmit,
  handleTryExample,
  isDisabled,
  isError,
  messages,
  ownDataRef,
  preSubmitCheck,
  progress,
  request,
  resetTaskProgress,
  taskStatus
}: CampaignTryOwnDataProps) => {
  const [ownData, setOwnData] = useState<string[]>([InputOutputExamples[0].label])
  const [currentOwnDataInput, setCurrentOwnDataInput] = useState<string>('')
  const [currentOwnDataOutput, setCurrentOwnDataOutput] = useState<string>('')
  const [inputTypeOwnData, setInputTypeOwnData] = useState<string | undefined>(
    InputOutputExamples[0].inputType
  )

  const handleOwnDataClick = (key: string) => {
    setOwnData([key])
    const ownData = InputOutputExamples.find(ownData => ownData.label === key)!
    setInputTypeOwnData(ownData.inputType)
  }

  const handleEditorSourceChange = (value: string) => {
    setCurrentOwnDataInput(value)
  }

  const handleEditorOutputChange = (value: string) => {
    setCurrentOwnDataOutput(value)
  }

  const isTaskCompleted = taskStatus === AgentTaskStatus.DOCUMENT_GENERATED

  return (
    <div ref={ownDataRef} className='h-fit w-screen flex items-center justify-center pt-20 pb-5'>
      <Card className='max-w-screen w-10/12 p-6 bg-zinc-50' shadow='sm'>
        <CardHeader className='flex gap-8 caret-transparent'>
          <div>
            <p className='text-5xl tracking-tighter'>Watch Curie write DataWeave</p>
            <p className='text-5xl tracking-tighter bg-gradient-to-r from-[#8063FA] to-[#C8A7FF] text-transparent bg-clip-text'>
              tailor-made for your sample data
            </p>
            <div className='mt-6'>
              <p>1. Enter your own sample input data.</p>
              <p>2. Enter your own sample output data.</p>
              <p>3. Click Generate.</p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className='flex flex-col gap-3'>
            <CampaignInputOutputToDWLForm
              key={'Try_your_own_data_' + String(inputTypeOwnData)}
              minRows={15}
              maxRows={15}
              onChange={(task: TaskParams) => handleSetRequest(task.request)}
              inputDefaultValue={currentOwnDataInput}
              outputDefaultValue={currentOwnDataOutput}
              inputTypeDefaultValue={inputTypeOwnData}
              inputClassName='h-[320px]'
              handleEditorSourceChange={handleEditorSourceChange}
              handleEditorOutputChange={handleEditorOutputChange}
              collapsible
              onError={() => handleSetError(true)}
              onErrorClear={() => handleSetError(false)}
            />

            <div className='w-full flex justify-center mt-4'>
              <CampaignButton
                id='create-account'
                type='submit'
                className='w-36 h-12 disabled:opacity-60'
                isDisabled={isDisabled || isError}
                onClick={() => {
                  if (!preSubmitCheck(request, 'ownData')) return
                  resetTaskProgress()
                  if (ownDataRef?.current)
                    ownDataRef.current.scrollIntoView({ behavior: 'instant', block: 'start' })
                  handleChangeGenerationSection('ownData')
                  handleSubmit(request)
                }}
              >
                <div className='flex flex-row gap-2'>
                  <IconCurieStarButton className='w-5 h-5' />
                  Generate
                </div>
              </CampaignButton>
            </div>
            <div className='w-full py-4 flex'>
              {currentGenerationSection === 'ownData' &&
              taskStatus !== AgentTaskStatus.NONE_TASK_STATUS ? (
                <CampaignTaskProgressBar taskStatus={taskStatus} percentage={progress} />
              ) : null}
            </div>
            {currentGenerationSection === 'ownData' &&
            Array.isArray(messages) &&
            messages.length ? (
              <CampaignChat messages={messages} />
            ) : null}
            {currentGenerationSection === 'ownData' && isTaskCompleted && (
              <CampaignButton onClick={handleTryExample} className='w-56 h-12 ml-10 -mt-6'>
                Try another sample
              </CampaignButton>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

const DiagramGenerator = ({
  currentGenerationSection,
  handleChangeGenerationSection,
  handleSetError,
  handleSetRequest,
  handleSubmit,
  handleTryExample,
  isDisabled,
  isError,
  messages,
  ownDataRef,
  preSubmitCheck,
  progress,
  request,
  resetTaskProgress,
  taskStatus
}: CampaignTryOwnDataProps) => {
  const [uploadPath, setUploadPath] = useState<string | undefined>(undefined)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const handleSelectionChange = (newSelection: string[]) => {
    setSelectedKeys(newSelection)
  }
  const flowNames = useFlowNamesCampaign({ s3TarFilePath: uploadPath ?? '' })
  const hasRequiredFields = Boolean(uploadPath)
  const hasFetchedFlowNames = !hasRequiredFields && flowNames === null
  const isLoading = Boolean(hasRequiredFields && flowNames === null)
  const hasFlowNames = flowNames && flowNames.length > 0
  const flowNamesString: string = selectedKeys.join(', ')
  const placeholderMessages = {
    initial: {
      none: '',
      codeContext: 'Select repository details to view flows',
      upload: 'Upload a repo to view flows',
      mappingTable: 'Select repository details to view flows'
    },
    loading: 'Loading flow names...',
    done: 'Select one or more flows',
    noFlows: 'No flows found'
  }

  const getPlaceholder = () => {
    if (isLoading) return placeholderMessages.loading
    else if (hasFetchedFlowNames) return placeholderMessages.initial['upload']
    else if (hasFlowNames) return placeholderMessages.done
    return placeholderMessages.noFlows
  }
  const items = (flowNames ?? []).map(flowName => ({
    value: flowName,
    label: flowName
  }))

  useAsync(async () => {
    const request = AgentRequest.fromPartial({
      command: { commandString: flowNamesString },
      metadata: {
        codeContext: {
          connType: 'S3',
          s3TarFilePath: uploadPath
        }
      },
      runtimeSettings: RuntimeSettings.fromPartial({
        javaVersion: '8.0',
        mavenVersion: '3.8'
      })
    })
    handleSetRequest(request)
  }, [currentGenerationSection, uploadPath, flowNamesString])
  const isTaskCompleted = taskStatus === AgentTaskStatus.DOCUMENT_GENERATED

  return (
    <div ref={ownDataRef} className='h-fit w-screen flex items-center justify-center pt-20 pb-5'>
      <Card className='max-w-screen w-10/12 p-6 bg-zinc-50' shadow='sm'>
        <CardHeader className='flex gap-8 caret-transparent'>
          <div>
            <p className='text-5xl tracking-tighter'>Watch Curie generate Diagram</p>
            <p className='text-5xl tracking-tighter bg-gradient-to-r from-[#8063FA] to-[#C8A7FF] text-transparent bg-clip-text'>
              tailor-made for your sample data
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className='flex flex-col gap-3'>
            <MuleProjectUpload onComplete={setUploadPath} withExamplePrefilled={false} />

            <SimpleMultiSelect
              label={'Flow Name(s)'}
              placeholder={getPlaceholder()}
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
              disabled={isLoading}
              items={items}
              isMultiSelect={true}
              size='sm'
            />
            <div className='w-full flex justify-center mt-4'>
              <CampaignButton
                id='create-account'
                type='submit'
                className='w-36 h-12 disabled:opacity-60'
                isDisabled={isDisabled || isError}
                onClick={() => {
                  if (!preSubmitCheck(request, 'ownData')) return
                  resetTaskProgress()
                  if (ownDataRef?.current)
                    ownDataRef.current.scrollIntoView({ behavior: 'instant', block: 'start' })
                  handleChangeGenerationSection('ownData')
                  handleSubmit(request)
                }}
              >
                <div className='flex flex-row gap-2'>
                  <IconCurieStarButton className='w-5 h-5' />
                  Generate
                </div>
              </CampaignButton>
            </div>
            <div className='w-full py-4 flex'>
              {currentGenerationSection === 'ownData' &&
              taskStatus !== AgentTaskStatus.NONE_TASK_STATUS ? (
                <CampaignTaskProgressBar taskStatus={taskStatus} percentage={progress} />
              ) : null}
            </div>
            {currentGenerationSection === 'ownData' &&
            Array.isArray(messages) &&
            messages.length ? (
              <CampaignChat messages={messages} />
            ) : null}
            {currentGenerationSection === 'ownData' && isTaskCompleted && (
              <CampaignButton onClick={handleTryExample} className='w-56 h-12 ml-10 -mt-6'>
                Try another sample
              </CampaignButton>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

const CampaignTryOwnData = ({
  commandType,
  currentGenerationSection,
  handleChangeGenerationSection,
  handleSetError,
  handleSetRequest,
  handleSubmit,
  handleTryExample,
  isDisabled,
  isError,
  messages,
  ownDataRef,
  preSubmitCheck,
  progress,
  request,
  resetTaskProgress,
  taskStatus
}: CampaignTryOwnDataProps) => {
  switch (commandType) {
    case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION:
      return (
        <DataWeaveGenerator
          currentGenerationSection={currentGenerationSection}
          handleChangeGenerationSection={handleChangeGenerationSection}
          handleSetError={handleSetError}
          handleSetRequest={handleSetRequest}
          handleSubmit={handleSubmit}
          handleTryExample={handleTryExample}
          isDisabled={isDisabled}
          isError={isError}
          messages={messages}
          ownDataRef={ownDataRef}
          preSubmitCheck={preSubmitCheck}
          progress={progress}
          request={request}
          resetTaskProgress={resetTaskProgress}
          taskStatus={taskStatus}
        />
      )
    case CommandType.GENERATE_IMAGE:
      return (
        <DiagramGenerator
          currentGenerationSection={currentGenerationSection}
          handleChangeGenerationSection={handleChangeGenerationSection}
          handleSetError={handleSetError}
          handleSetRequest={handleSetRequest}
          handleSubmit={handleSubmit}
          handleTryExample={handleTryExample}
          isDisabled={isDisabled}
          isError={isError}
          messages={messages}
          ownDataRef={ownDataRef}
          preSubmitCheck={preSubmitCheck}
          progress={progress}
          request={request}
          resetTaskProgress={resetTaskProgress}
          taskStatus={taskStatus}
        />
      )
    default:
      return null
  }
}

export default CampaignTryOwnData
